/**
 * Gift match summary
 *
 * A read-only view of the gift match a pool is created from: its settings, conditions and matched
 * total, with a link to change it on its campaign and a refresh to show the change.
 */

import React, { useId } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import CurrencyHelper from '../../../../helpers/currencyHelper';
import {
  getFullImagePathByType,
  uploadTypes
} from '../../../../constants/imageUpload';
import { giftMatchingKeys, i18nKeys } from '../constants';
import { formatConditionDateTime, toConditionFormValues } from '../helpers';
import { CampaignGiftMatch, conditionOperator } from '../types';
import Styles from '../GiftMatchingPools.module.css';

/** The gift match's conditions in words, one line each. */
export const describeConditions = (
  conditions: CampaignGiftMatch['conditions'],
  currency: string | null
): string[] => {
  const values = toConditionFormValues(conditions);
  const money = (value: string) =>
    CurrencyHelper.formatCurrency(Number(value), currency);
  const line = (labelKey: string, value: string) =>
    I18n.t(i18nKeys.SUMMARY.CONDITION_VALUE, {
      label: I18n.t(labelKey),
      value
    });

  const lines: string[] = [];
  if (values.startsAt.enabled) {
    lines.push(
      line(
        giftMatchingKeys.CONDITIONS.START,
        formatConditionDateTime(values.startsAt.value)
      )
    );
  }
  if (values.endsAt.enabled) {
    lines.push(
      line(
        giftMatchingKeys.CONDITIONS.END,
        formatConditionDateTime(values.endsAt.value)
      )
    );
  }
  if (values.giftMatchingTotal.enabled) {
    lines.push(
      line(
        giftMatchingKeys.CONDITIONS.GIFT_MATCHING_TOTAL,
        money(values.giftMatchingTotal.value)
      )
    );
  }
  if (values.campaignTotal.enabled) {
    lines.push(
      line(
        giftMatchingKeys.CONDITIONS.CAMPAIGN_TOTAL,
        money(values.campaignTotal.value)
      )
    );
  }
  if (values.donationCount.enabled) {
    lines.push(
      line(
        giftMatchingKeys.CONDITIONS.DONATION_COUNT,
        values.donationCount.value
      )
    );
  }
  const amount = values.donationAmount;
  if (amount.enabled) {
    const operator = I18n.t(
      `${giftMatchingKeys.CONDITIONS.DONATION_AMOUNT_OPERATOR}.${amount.operator}.label`
    );
    const amounts =
      amount.operator === conditionOperator.between
        ? I18n.t(i18nKeys.SUMMARY.AMOUNT_RANGE, {
            from: money(amount.from),
            to: money(amount.to)
          })
        : money(amount.from);
    lines.push(
      line(
        giftMatchingKeys.CONDITIONS.DONATION_AMOUNT,
        `${operator}, ${amounts}`
      )
    );
  }
  return lines;
};

interface GiftMatchSummaryProps {
  campaignId: number;
  currency: string | null;
  giftMatch: CampaignGiftMatch;
  onRefresh: () => void;
  refreshing?: boolean;
}

const GiftMatchSummary: React.FC<GiftMatchSummaryProps> = ({
  campaignId,
  currency,
  giftMatch,
  onRefresh,
  refreshing
}) => {
  const titleId = useId();
  const notSet = I18n.t(i18nKeys.SUMMARY.NOT_SET);
  const conditions = describeConditions(giftMatch.conditions, currency);
  const imageUrl = giftMatch.mainImagePath
    ? getFullImagePathByType(giftMatch.mainImagePath, uploadTypes.LOGO)
    : '';

  return (
    <section className={Styles.summary} aria-labelledby={titleId}>
      <div className={Styles.summaryHeader}>
        <h4 id={titleId}>
          <Translate value={i18nKeys.SUMMARY.TITLE} />
        </h4>
        <div className={Styles.summaryActions}>
          <Link
            className="ui basic tiny button"
            to={`/campaigns/edit/${campaignId}/giftMatching`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Translate value={i18nKeys.SUMMARY.EDIT_ON_CAMPAIGN} />
            <span className={Styles.srOnly}>
              {` ${I18n.t(i18nKeys.SUMMARY.NEW_TAB)}`}
            </span>
          </Link>
          <Button
            basic
            size="tiny"
            type="button"
            loading={refreshing}
            disabled={refreshing}
            onClick={onRefresh}
          >
            <Translate value={i18nKeys.SUMMARY.REFRESH} />
          </Button>
        </div>
      </div>
      <p className="field-description">
        <Translate value={i18nKeys.SUMMARY.HELP} />
      </p>
      <dl className={Styles.summaryList}>
        <dt>{I18n.t(giftMatchingKeys.NAME)}</dt>
        <dd>{giftMatch.name || notSet}</dd>
        <dt>{I18n.t(i18nKeys.LIST.MULTIPLIER)}</dt>
        <dd>
          {I18n.t(i18nKeys.LIST.MULTIPLIER_VALUE, {
            multiplier: giftMatch.multiplier
          })}
        </dd>
        <dt>{I18n.t(i18nKeys.LIST.STATUS)}</dt>
        <dd>
          {I18n.t(giftMatch.enabled ? i18nKeys.LIST.ON : i18nKeys.LIST.OFF)}
        </dd>
        <dt>{I18n.t(i18nKeys.FIELDS.CONDITIONS_TITLE)}</dt>
        <dd>
          {conditions.length > 0 ? (
            <ul className={Styles.summaryConditions}>
              {conditions.map((condition) => (
                <li key={condition}>{condition}</li>
              ))}
            </ul>
          ) : (
            I18n.t(i18nKeys.SUMMARY.NO_CONDITIONS)
          )}
        </dd>
        <dt>{I18n.t(giftMatchingKeys.DONATION_FROM)}</dt>
        <dd>{giftMatch.donation?.from || notSet}</dd>
        <dt>{I18n.t(giftMatchingKeys.DONATION_MESSAGE)}</dt>
        <dd>{giftMatch.donation?.message || notSet}</dd>
        <dt>{I18n.t(giftMatchingKeys.IMAGE)}</dt>
        <dd>
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={giftMatch.name}
              className={Styles.summaryImage}
            />
          ) : (
            notSet
          )}
        </dd>
        <dt>{I18n.t(i18nKeys.LIST.MATCHED)}</dt>
        <dd>
          {I18n.t(i18nKeys.LIST.MATCHED_VALUE, {
            amount: CurrencyHelper.formatCurrency(
              giftMatch.donationMatchedTotalAmount,
              currency
            ),
            count: giftMatch.donationMatchedCount
          })}
        </dd>
      </dl>
    </section>
  );
};

export default GiftMatchSummary;
